import { useEffect, useState } from 'react';
import { useGetAllEventsQuery } from '../store';
import LandingEventCard from '../components/LandingEventCard';
import EventDTO from '../models/event';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import AvailableEventsPageSkeleton from '../components/AvailableEventsPageSkeleton';
import Carrousel from '../components/atoms/Carrousel/Carrousel';
import { BusinessDTO } from '../models/business';
import BusinessMemberCards from '../components/BusinessMemberCards';
import { IconEnjoy1 } from '../components/icons';
import CardsLottie from '../components/CardsLottie';

interface Props {
  loadedEvents?: EventDTO[];
  business?: BusinessDTO;
}

const AvailableEventsPage: React.FC<Props> = ({ loadedEvents, business }) => {
  const navigate = useNavigateWithQuery();
  const { data: events, isLoading } = useGetAllEventsQuery(
    {},
    { skip: !!loadedEvents },
  );
  const [sortedEvents, setSortedEvents] = useState<EventDTO[]>([]);
  const [images, setImages] = useState<{ url: string; eventId: string }[]>([]);

  useEffect(() => {
    const eventsToShow = loadedEvents || events;
    if (eventsToShow) {
      const PRIORITY_BUSINESS_IDS = ['6a36f940-3ec6-4a59-9fe4-64c6982c15d2', '59b5a378-643a-4d59-b06c-b72a74e46aff'];
      const sorted = [...eventsToShow]
        .filter((e) => e.public_display)
        .sort((a, b) => {
          const aPriority = PRIORITY_BUSINESS_IDS.includes(a.business_id) ? 0 : 1;
          const bPriority = PRIORITY_BUSINESS_IDS.includes(b.business_id) ? 0 : 1;
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          const aTime = new Date(a.date).getTime();
          const bTime = new Date(b.date).getTime();
          return aTime - bTime;
        });
      setSortedEvents(sorted);
      const bannerEvents = sorted.filter((event) => event.banner_display).length > 0 ? sorted.filter((event) => event.banner_display) : sorted;
      setImages(bannerEvents.length > 0 ? bannerEvents.map((event) => ({
        url: event.banner_image || '/avantti-default-event.png',
        eventId: event.slug,
      })) : [{
        url: '/avantti-default-event.png',
        eventId: '',
      }]);
    }
  }, [events, loadedEvents]);

  const handleEventSelect = (eventId: string) => {
    if (eventId) {
      navigate(`/events/${eventId}`);
    }
  };

  return (
    <div className="flex flex-col gap-6 py-5 h-full min-h-screen">
      {isLoading
        && <AvailableEventsPageSkeleton />}
      {!isLoading && images.length !== 0 && (
        <Carrousel
          images={images}
          onImageClick={(eventId) => handleEventSelect(eventId)}
          isArrow={true}
        />
      )}
      {!business?.is_white_label && <div>
        <CardsLottie />
      </div>}

      {loadedEvents && business?.has_members
        && <BusinessMemberCards business={business} />}

      {sortedEvents.length > 0 && (
        <>
          <div className="flex flex-row items-center gap-4">
            <span className="text-xl-semibold lg:display-md-semibold text-text-primary">
              Próximos eventos
            </span>
            <IconEnjoy1 className="w-10 h-10" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-20 gap-6 items-center flex-wrap">
            {!isLoading
              && sortedEvents.map((event) => (
                <div key={event.id} className="">
                  <LandingEventCard event={event} onSelect={handleEventSelect} textToast={event.is_sold_out ? 'Entradas agotadas' : undefined} colorToast={event.is_sold_out ? 'bg-warning-300' : undefined} />
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AvailableEventsPage;
